/**
 * How the SPA draws an external login provider. The server names a scheme (`MSEntraID`,
 * `Google`, …) and its display name; what it cannot say is which glyph and which wording the
 * button should carry, and a button that reads "Sign in with Microsoft Entra ID" beside a
 * "Sign in with Google" is the display name doing a job it was not written for. So the
 * scheme-keyed presentation lives here, once, for the login page and every other place that
 * offers a provider - and a scheme this table does not know still gets a button: a generic
 * icon and the display name in a generic label.
 */

export type ProviderIcon = [prefix: string, name: string];

interface ProviderPresentation {
  icon: ProviderIcon;
  /** i18n key of the button label; without one, `page.login.signinWith` takes the display name. */
  labelKey?: string;
}

const presentations: Record<string, ProviderPresentation> = {
  MSEntraID: { icon: ["fab", "microsoft"], labelKey: "page.login.signinWithEntra" },
  Google: { icon: ["fab", "google"], labelKey: "page.login.signinWithGoogle" },
};

const fallbackIcon: ProviderIcon = ["fas", "arrow-right-to-bracket"];

/** The glyph for a scheme; a generic sign-in arrow for one this table does not know. */
export function externalProviderIcon(scheme: string): ProviderIcon {
  return presentations[scheme]?.icon ?? fallbackIcon;
}

/**
 * The login button's label for a scheme: the scheme's own key where one is registered, else the
 * generic "Sign in with {provider}" over the server's display name.
 */
export function externalProviderLabel(
  scheme: string,
  displayName: string,
  t: (key: string, values?: Record<string, unknown>) => string,
): string {
  const key = presentations[scheme]?.labelKey;
  return key ? t(key) : t("page.login.signinWith", { provider: displayName });
}
